import { createFileRoute } from '@tanstack/react-router'

import {
  InviteSheet,
  MetaConnect,
  ProfessionalGuideSheet,
  SETUP_STEPS,
  WizardStep,
} from '@/components/onboarding'
import { ACCOUNTS } from '@/mocks/onboarding/napojeni'

/**
 * `stav` is how far the OAuth got, `info` is which drawer sits on top —
 * the two are independent, so both are their own param.
 */
type NapojeniSearch = { stav?: 'pripojeno'; info?: 'prevod' | 'pozvanka' }

const PAGE_HREF = '/onboarding/napojeni'
const CONNECTED_HREF = `${PAGE_HREF}?stav=pripojeno`
const GUIDE_HREF = `${PAGE_HREF}?info=prevod`
const INVITE_HREF = `${PAGE_HREF}?info=pozvanka`

export const Route = createFileRoute('/onboarding/napojeni')({
  validateSearch: (search: Record<string, unknown>): NapojeniSearch => ({
    ...(search.stav === 'pripojeno' ? { stav: 'pripojeno' as const } : {}),
    ...(search.info === 'prevod' || search.info === 'pozvanka'
      ? { info: search.info as 'prevod' | 'pozvanka' }
      : {}),
  }),
  head: () => ({ meta: [{ title: 'Napojení sítí · Aplikace Bitez' }] }),
  component: OnboardingNapojeni,
})

/**
 * Connecting Meta (spec §3) — in the **second** run, not the first (owner,
 * 2026-08-20). It is the textbook case of what the first three minutes must not
 * ask: admin rights to a Facebook page, frequently a different person than the
 * one holding the phone, and by far the most common place an onboarding stalls.
 * As the last of five steps it broke everything before it; here it is second,
 * and the overview keeps bringing the client back to it.
 *
 * Spec §3.1 asks for the explainer BEFORE the OAuth, and this screen takes that
 * literally. The "nedotkneme" half is deliberately **padlocks rather than
 * crosses** — a cross is a scolding, a padlock is a promise — and the line that
 * actually calms people is "heslo nevidíme, přihlašujete se u nich".
 *
 * **The button is the OAuth**, so in the prototype it lands on the connected
 * state. Both real-world detours are drawers rather than dead ends: a personal
 * account that has to become Professional (§3.2) and "the rights aren't mine"
 * (§3.5).
 */
function OnboardingNapojeni() {
  const { stav, info } = Route.useSearch()
  const connected = stav === 'pripojeno'

  return (
    <WizardStep
      step={2}
      steps={SETUP_STEPS}
      backHref="/onboarding/znacka"
      note={
        connected
          ? 'Účty jsou napojené. Odebrat přístup jde kdykoliv v Nastavení.'
          : 'Bez napojení nemůžeme publikovat. Chceme jen to, co je k tomu potřeba — nic víc.'
      }
      nextHref={connected ? '/onboarding/odkazy' : CONNECTED_HREF}
      nextLabel={connected ? 'Pokračovat' : 'Připojit Instagram a Facebook'}
      skip={connected ? undefined : { label: 'Přístup k účtům nemám já', href: INVITE_HREF }}
      sheet={
        <>
          {info === 'prevod' && <ProfessionalGuideSheet closeHref={PAGE_HREF} />}
          {info === 'pozvanka' && <InviteSheet closeHref={PAGE_HREF} />}
        </>
      }
    >
      <MetaConnect accounts={connected ? ACCOUNTS : undefined} guideHref={GUIDE_HREF} />
    </WizardStep>
  )
}
